import React, { useState } from 'react';
import { BoxStyled, TypographyStyledHeader } from '../patientInformation/style';
import { useAppSelector } from '../../store/hooks';
import { metaDataSelector } from '../../store/globalSelectors';
import { NavigationButtons } from '../../shared/components/navigationButtons';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Modal,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  StyledTableCell,
  StyledTableRow,
  TypographyBg20BlueBold,
  TypographyCalibri12Bold,
  TypographyCalibri12Normal
} from '../compare/style';
import {
  calculateRNISummedValue,
  formatNumber,
  handleDownloadPdf,
  handleSharePdf,
  roundToPrecision
} from '../../shared/utils/utilFunctions';
import {
  compareProductsRniValues,
  rniScreenTileData
} from '../../shared/shared.constants';

import { TileSleekGridGroup } from '../../shared/components/tileSleekGridGroup';
import EmailResultsIcon from '../../assets/icons/emailResults.svg';
import DownloadIcon from '../../assets/icons/downloadIcon.svg';
import infoIcon from '../../assets/icons/infoIcon.svg';
import { TableInfoModal } from './TableInfoModal';

const TableView = () => {
  // Always use the same keys so that the Accordion remains controlled.
  const [expandedAccordions, setExpandedAccordions] = useState({
    nutrients: true,
    vitamins: false,
    vitamins2: false,
    minerals: false,
    minerals2: false
  });
  // For showing a loading state if needed
  const [loading, setLoading] = useState(false);
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const handleIconClick = () => {
    setOpenInfoModal((prev: boolean) => !prev);
  };
  const { rni, patientInformation, summary } = useAppSelector(metaDataSelector);
  const {
    selectedAdultEnergy,
    selectedPaediatricEnergy,
    selectedPaediatricProtein,
    estimatingRequirements
  } = summary;
  const { protein } = estimatingRequirements;
  const {
    selectedProducts,
    p1VolumeCalculationOffset,
    p2VolumeCalculationOffset
  } = rni;
  const { age, gender } = patientInformation;
  const { years: patientAgeYears, months: patientAgeMonths } = age;

  let userAgeInMonths = 0;
  if (!patientAgeYears && !patientAgeMonths) return;
  if (!patientAgeYears && patientAgeMonths) {
    userAgeInMonths = patientAgeMonths;
  } else {
    userAgeInMonths = patientAgeYears * 12;
  }

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordions({ ...expandedAccordions, [panel]: isExpanded });
  };

  const scaleNutrientValues = (product, userVolume) => {
    const originalVolume = parseFloat(product.volumeOffset);
    const scalingFactor = userVolume / originalVolume;
    const scaledProduct = JSON.parse(JSON.stringify(product));
    const scaleNestedValues = (obj) => {
      for (const key in obj) {
        if (typeof obj[key] === 'object' && obj[key] !== null) {
          scaleNestedValues(obj[key]);
        } else if (key === 'value') {
          if (obj[key] === 'N/A' || isNaN(Number(obj[key]))) {
            continue;
          }
          obj[key] = roundToPrecision(Number(obj[key] * scalingFactor));
        }
      }
    };
    scaleNestedValues(scaledProduct.macroNutrients);
    scaleNestedValues(scaledProduct.vitamins);
    scaleNestedValues(scaledProduct.minerals);
    return scaledProduct;
  };

  const getRniCategory = () => {
    const isAdult = userAgeInMonths >= 216;
    const isFemale = gender.toLowerCase() === 'female';
    if (isAdult) {
      return isFemale
        ? userAgeInMonths >= 600
          ? 'rniFemale50Plus'
          : 'rniFemale19To50'
        : userAgeInMonths >= 600
          ? 'rniMale50Plus'
          : 'rniMale19To50';
    } else {
      return 'paed';
    }
  };

  const getPaediatricAgeGroup = (ageInMonths, nutrientKey) => {
    if (nutrientKey === 'magnesium' || nutrientKey === 'copper') {
      if (ageInMonths >= 12 && ageInMonths <= 24) return '1To2';
      if (ageInMonths >= 25 && ageInMonths <= 108) return '3To9';
      if (ageInMonths >= 109) return '10To17';
    }
    if (ageInMonths >= 7 && ageInMonths <= 11) return '7to11m';
    if (ageInMonths >= 12 && ageInMonths <= 36) return '1To3';
    if (ageInMonths >= 37 && ageInMonths <= 60) return '4To5';
    if (ageInMonths >= 61 && ageInMonths <= 120) return '7To10';
    if (ageInMonths >= 121 && ageInMonths <= 168) return '11To14';
    if (ageInMonths >= 169 && ageInMonths < 216) return '15to17';
    return null;
  };

  const getRniValue = (nutrientKey) => {
    const rniCategory = getRniCategory();
    if (rniCategory === 'paed') {
      const paedData = compareProductsRniValues.paed;
      if (typeof paedData[nutrientKey] !== 'object') {
        return paedData[nutrientKey] || 'N/A';
      }
      const ageGroup = getPaediatricAgeGroup(userAgeInMonths, nutrientKey);
      const genderPrefix =
        gender.toLowerCase() === 'female' ? 'rniFemale' : 'rniMale';
      const rniKey = `${genderPrefix}${ageGroup}`;
      return paedData[nutrientKey][rniKey] || 'N/A';
    }
    const adultRniValues = compareProductsRniValues.adults[rniCategory];
    return adultRniValues[nutrientKey] ?? 'N/A';
  };

  const renderMacronutrientsRNITable = () => {
    if (selectedProducts.length === 0) return null;

    const product1 = selectedProducts[0];
    const product2 = selectedProducts.length > 1 ? selectedProducts[1] : null;
    const userVolume1 = p1VolumeCalculationOffset;
    const userVolume2 = p2VolumeCalculationOffset;
    const scaledProduct1 = scaleNutrientValues(product1, userVolume1);
    const scaledProduct2 = product2
      ? scaleNutrientValues(product2, userVolume2)
      : null;

    const ENERGY_RNI =
      userAgeInMonths >= 216
        ? selectedAdultEnergy?.value
        : selectedPaediatricEnergy?.value;
    const PROTEIN_RNI =
      userAgeInMonths >= 216
        ? `${protein?.min} - ${protein?.max}`
        : roundToPrecision(selectedPaediatricProtein?.value);
    const nutrients = [
      { label: 'Energy', key: 'energy', units: ['kj', 'kcal'] },
      { label: 'Protein', key: 'protein' },
      { label: 'Fibre', key: 'fibre' }
    ];

    return (
      <div
        id="section-macronutrients"
        style={{ background: '#fff' }}
        className="pdf-section"
      >
        <Accordion
          id="nutrients"
          expanded={expandedAccordions.nutrients}
          onChange={handleAccordionChange('nutrients')}
          sx={{
            marginTop: '0.625rem',
            marginBottom: '0px',
            boxShadow: 'none',
            border: '0.0625rem solid #8BB5C6',
            breakInside: 'avoid'
          }}
          className="accordion-pdf-spacing"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <TypographyBg20BlueBold>MACRONUTRIENTS</TypographyBg20BlueBold>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: 0 }}>
            <TableContainer
              component={Paper}
              sx={{ boxShadow: 'none', border: 'none' }}
            >
              <Table className="pdf-table">
                <TableHead>
                  <TableRow className="pdf-row">
                    <StyledTableCell></StyledTableCell>
                    <StyledTableCell>
                      <TypographyCalibri12Bold
                        sx={{
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          width: 'auto'
                        }}
                      >
                        Unit
                      </TypographyCalibri12Bold>
                    </StyledTableCell>
                    <StyledTableCell sx={{ backgroundColor: '#DEF3FA' }}>
                      <TypographyCalibri12Bold
                        sx={{
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          width: 'auto'
                        }}
                      >
                        {selectedProducts.length > 1
                          ? `${userVolume1} mL ${product1.name} & ${userVolume2} mL ${product2?.name}`
                          : `${userVolume1} mL ${product1.name}`}
                      </TypographyCalibri12Bold>
                    </StyledTableCell>
                    <StyledTableCell sx={{ backgroundColor: '#CCE4F5' }}>
                      <TypographyCalibri12Bold
                        sx={{
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          width: 'auto'
                        }}
                      >
                        Requirements
                      </TypographyCalibri12Bold>
                    </StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {nutrients.map((nutrient) => {
                    const product1Nutrient =
                      scaledProduct1.macroNutrients[nutrient.key];
                    const product2Nutrient = scaledProduct2?.macroNutrients?.[
                      nutrient.key
                    ] || {
                      value: 0,
                      unit: product1Nutrient.unit
                    };

                    if (nutrient.key === 'energy') {
                      return (
                        <React.Fragment key={nutrient.key}>
                          {nutrient.units.map((unit, index) => (
                            <StyledTableRow key={unit}>
                              <StyledTableCell sx={{ minWidth: '130px' }}>
                                <TypographyCalibri12Normal
                                  sx={{
                                    verticalAlign: 'middle',
                                    textAlign: 'left',
                                    width: 'auto'
                                  }}
                                >
                                  {index === 0 ? nutrient.label : ''}
                                </TypographyCalibri12Normal>
                              </StyledTableCell>
                              <StyledTableCell>
                                <TypographyCalibri12Normal
                                  sx={{
                                    verticalAlign: 'middle',
                                    textAlign: 'center',
                                    width: 'auto'
                                  }}
                                >
                                  {product1Nutrient[unit].unit}
                                </TypographyCalibri12Normal>
                              </StyledTableCell>
                              <StyledTableCell
                                sx={{ backgroundColor: '#DEF3FA' }}
                              >
                                <TypographyCalibri12Normal
                                  sx={{
                                    verticalAlign: 'middle',
                                    textAlign: 'center',
                                    width: 'auto'
                                  }}
                                >
                                  {calculateRNISummedValue(
                                    product1Nutrient[unit].value,
                                    product2Nutrient[unit]?.value || null
                                  )}
                                </TypographyCalibri12Normal>
                              </StyledTableCell>

                              {index === 0 && (
                                <StyledTableCell
                                  sx={{ backgroundColor: '#CCE4F5' }}
                                >
                                  <TypographyCalibri12Normal
                                    sx={{
                                      verticalAlign: 'middle',
                                      textAlign: 'center',
                                      width: 'auto'
                                    }}
                                  >
                                    {' '}
                                    N/A
                                  </TypographyCalibri12Normal>
                                </StyledTableCell>
                              )}

                              {index === 1 && (
                                <StyledTableCell
                                  sx={{ backgroundColor: '#CCE4F5' }}
                                >
                                  <TypographyCalibri12Normal
                                    sx={{
                                      verticalAlign: 'middle',
                                      textAlign: 'center',
                                      width: 'auto'
                                    }}
                                  >
                                    {' '}
                                    {ENERGY_RNI}
                                  </TypographyCalibri12Normal>
                                </StyledTableCell>
                              )}
                            </StyledTableRow>
                          ))}
                        </React.Fragment>
                      );
                    }

                    return (
                      <React.Fragment key={nutrient.key}>
                        <StyledTableRow>
                          <StyledTableCell sx={{ minWidth: '130px' }}>
                            {' '}
                            <TypographyCalibri12Normal
                              sx={{
                                verticalAlign: 'middle',
                                textAlign: 'left',
                                width: 'auto'
                              }}
                            >
                              {nutrient.label}
                            </TypographyCalibri12Normal>
                          </StyledTableCell>
                          <StyledTableCell>
                            <TypographyCalibri12Normal
                              sx={{
                                verticalAlign: 'middle',
                                textAlign: 'center',
                                width: 'auto'
                              }}
                            >
                              {product1Nutrient.unit}
                            </TypographyCalibri12Normal>
                          </StyledTableCell>
                          <StyledTableCell sx={{ backgroundColor: '#DEF3FA' }}>
                            <TypographyCalibri12Normal
                              sx={{
                                verticalAlign: 'middle',
                                textAlign: 'center',
                                width: 'auto'
                              }}
                            >
                              {calculateRNISummedValue(
                                product1Nutrient.value,
                                product2Nutrient?.value || null
                              )}
                            </TypographyCalibri12Normal>
                          </StyledTableCell>
                          <StyledTableCell sx={{ backgroundColor: '#CCE4F5' }}>
                            <TypographyCalibri12Normal
                              sx={{
                                verticalAlign: 'middle',
                                textAlign: 'center',
                                width: 'auto'
                              }}
                            >
                              {nutrient.key === 'protein'
                                ? PROTEIN_RNI
                                : nutrient.key === 'fibre'
                                  ? getRniValue('fibre')
                                  : 'N/A'}
                            </TypographyCalibri12Normal>
                          </StyledTableCell>
                        </StyledTableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      </div>
    );
  };

  const renderVitaminsRNITable = () => {
    if (selectedProducts.length === 0) return null;

    const product1 = selectedProducts[0];
    const product2 = selectedProducts.length > 1 ? selectedProducts[1] : null;
    const userVolume1 = p1VolumeCalculationOffset;
    const userVolume2 = p2VolumeCalculationOffset;
    const scaledProduct1 = scaleNutrientValues(product1, userVolume1);
    const scaledProduct2 = product2
      ? scaleNutrientValues(product2, userVolume2)
      : null;

    const vitamins = [
      { label: 'Vitamin A (RE)', key: 'vitaminAre' },
      { label: 'Vitamin D', key: 'vitaminD' },
      { label: 'Vitamin E (α-TE)', key: 'vitaminE' },
      { label: 'Vitamin K<sup>*</sup>', key: 'vitaminK' },
      { label: 'Vitamin C', key: 'vitaminC' },
      { label: 'Folate<sup>**</sup>', key: 'folate' },
      { label: 'Folic Acid', key: 'folacin' },
      { label: 'Thiamin (vitamin B<sub>1</sub>)', key: 'thiaminVitaminB1' },
      {
        label: 'Riboflavin (vitamin B<sub>2</sub>)',
        key: 'riboflavinVitaminB2'
      },
      { label: 'Vitamin B<sub>6</sub><sup>¥</sup>', key: 'vitaminB6' },
      { label: 'Vitamin B<sub>12</sub>', key: 'vitaminB12' },
      { label: 'Niacin<sup>^</sup> NE', key: 'niacin' },
      { label: 'Pantothenic Acid<sup>†</sup>', key: 'panthothenicAcid' },
      { label: 'Biotin<sup>±</sup>', key: 'biotin' }
    ];

    const rniCategory = getRniCategory();
    const rniHeader =
      rniCategory === 'paed'
        ? 'RNI (Paediatric)'
        : rniCategory === 'rniFemale19To50'
          ? 'RNI Female 19-50 years'
          : rniCategory === 'rniFemale50Plus'
            ? 'RNI Female 50+ years'
            : rniCategory === 'rniMale19To50'
              ? 'RNI Male 19-50 years'
              : 'RNI Male 50+ years';

    return (
      <>
        <div
          id="section-vitamins"
          style={{ background: '#fff' }}
          className="pdf-section"
        >
          <Accordion
            id="vitamins"
            expanded={expandedAccordions.vitamins}
            onChange={handleAccordionChange('vitamins')}
            sx={{
              marginTop: '0.625rem',
              marginBottom: '0.625rem',
              boxShadow: 'none',
              border: '0.0625rem solid #8BB5C6',
              breakInside: 'avoid'
            }}
            className="accordion-pdf-spacing"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <TypographyBg20BlueBold>VITAMINS</TypographyBg20BlueBold>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              <TableContainer
                component={Paper}
                sx={{ boxShadow: 'none', border: 'none' }}
              >
                <Table className="pdf-table">
                  <TableHead>
                    <TableRow className="pdf-row">
                      <StyledTableCell></StyledTableCell>
                      <StyledTableCell>
                        <TypographyCalibri12Bold
                          sx={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            width: 'auto'
                          }}
                        >
                          Unit
                        </TypographyCalibri12Bold>
                      </StyledTableCell>
                      <StyledTableCell sx={{ backgroundColor: '#DEF3FA' }}>
                        <TypographyCalibri12Bold
                          sx={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            width: 'auto'
                          }}
                        >
                          {selectedProducts.length > 1
                            ? `${userVolume1} mL ${product1.name} & ${userVolume2} mL ${product2?.name}`
                            : `${userVolume1} mL ${product1.name}`}
                        </TypographyCalibri12Bold>
                      </StyledTableCell>
                      <StyledTableCell sx={{ backgroundColor: '#CCE4F5' }}>
                        <TypographyCalibri12Bold
                          sx={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            width: 'auto'
                          }}
                        >
                          {rniHeader}
                        </TypographyCalibri12Bold>
                      </StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vitamins.map((vitamin, index) => {
                      const product1Vitamin =
                        scaledProduct1.vitamins[vitamin.key];
                      const product2Vitamin = scaledProduct2?.vitamins?.[
                        vitamin.key
                      ] || {
                        value: 0,
                        unit: product1Vitamin.unit
                      };

                      const summedValue = calculateRNISummedValue(
                        product1Vitamin.value,
                        product2Vitamin?.value || null
                      );

                      return (
                        <React.Fragment key={vitamin.key}>
                          <StyledTableRow
                            className={index > 7 ? 'pdf-hidden-element' : ''}
                          >
                            <StyledTableCell sx={{ minWidth: '130px' }}>
                              <TypographyCalibri12Normal
                                sx={{
                                  verticalAlign: 'middle',
                                  textAlign: 'left',
                                  width: 'auto'
                                }}
                              >
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: vitamin.label
                                  }}
                                ></span>
                              </TypographyCalibri12Normal>
                            </StyledTableCell>
                            <StyledTableCell>
                              <TypographyCalibri12Normal
                                sx={{
                                  verticalAlign: 'middle',
                                  textAlign: 'center',
                                  width: 'auto'
                                }}
                              >
                                {product1Vitamin.unit}
                              </TypographyCalibri12Normal>
                            </StyledTableCell>
                            <StyledTableCell
                              sx={{ backgroundColor: '#DEF3FA' }}
                            >
                              <TypographyCalibri12Normal
                                sx={{
                                  verticalAlign: 'middle',
                                  textAlign: 'center',
                                  width: 'auto'
                                }}
                              >
                                {summedValue}
                              </TypographyCalibri12Normal>
                            </StyledTableCell>
                            <StyledTableCell
                              sx={{ backgroundColor: '#CCE4F5' }}
                            >
                              <TypographyCalibri12Normal
                                sx={{
                                  verticalAlign: 'middle',
                                  textAlign: 'center',
                                  width: 'auto'
                                }}
                              >
                                {formatNumber(getRniValue(vitamin.key))}
                              </TypographyCalibri12Normal>
                            </StyledTableCell>
                          </StyledTableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </div>
        <div
          id="section-vitamins-2"
          style={{ display: 'none', background: '#fff' }}
          className="pdf-section"
        >
          <Accordion
            id="vitamins"
            expanded={expandedAccordions.vitamins2}
            onChange={handleAccordionChange('vitamins2')}
            sx={{
              marginTop: '0.625rem',
              marginBottom: '0.625rem',
              boxShadow: 'none',
              border: '0.0625rem solid #8BB5C6',
              breakInside: 'avoid'
            }}
            className="accordion-pdf-spacing"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <TypographyBg20BlueBold>VITAMINS</TypographyBg20BlueBold>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              <TableContainer
                component={Paper}
                sx={{ boxShadow: 'none', border: 'none' }}
              >
                <Table className="pdf-table">
                  <TableHead>
                    <TableRow className="pdf-row">
                      <StyledTableCell></StyledTableCell>
                      <StyledTableCell>
                        <TypographyCalibri12Bold
                          sx={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            width: 'auto'
                          }}
                        >
                          Unit
                        </TypographyCalibri12Bold>
                      </StyledTableCell>
                      <StyledTableCell sx={{ backgroundColor: '#DEF3FA' }}>
                        <TypographyCalibri12Bold
                          sx={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            width: 'auto'
                          }}
                        >
                          {selectedProducts.length > 1
                            ? `${userVolume1} mL ${product1.name} & ${userVolume2} mL ${product2?.name}`
                            : `${userVolume1} mL ${product1.name}`}
                        </TypographyCalibri12Bold>
                      </StyledTableCell>
                      <StyledTableCell sx={{ backgroundColor: '#CCE4F5' }}>
                        <TypographyCalibri12Bold
                          sx={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            width: 'auto'
                          }}
                        >
                          {rniHeader}
                        </TypographyCalibri12Bold>
                      </StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vitamins.map((vitamin, index) => {
                      if (index < 8) return;
                      const product1Vitamin =
                        scaledProduct1.vitamins[vitamin.key];
                      const product2Vitamin = scaledProduct2?.vitamins?.[
                        vitamin.key
                      ] || {
                        value: 0,
                        unit: product1Vitamin.unit
                      };

                      const summedValue = calculateRNISummedValue(
                        product1Vitamin.value,
                        product2Vitamin?.value || null
                      );

                      return (
                        <React.Fragment key={vitamin.key}>
                          <StyledTableRow>
                            <StyledTableCell sx={{ minWidth: '130px' }}>
                              <TypographyCalibri12Normal
                                sx={{
                                  verticalAlign: 'middle',
                                  textAlign: 'left',
                                  width: 'auto'
                                }}
                              >
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: vitamin.label
                                  }}
                                ></span>
                              </TypographyCalibri12Normal>
                            </StyledTableCell>
                            <StyledTableCell>
                              <TypographyCalibri12Normal
                                sx={{
                                  verticalAlign: 'middle',
                                  textAlign: 'center',
                                  width: 'auto'
                                }}
                              >
                                {product1Vitamin.unit}
                              </TypographyCalibri12Normal>
                            </StyledTableCell>
                            <StyledTableCell
                              sx={{ backgroundColor: '#DEF3FA' }}
                            >
                              <TypographyCalibri12Normal
                                sx={{
                                  verticalAlign: 'middle',
                                  textAlign: 'center',
                                  width: 'auto'
                                }}
                              >
                                {summedValue}
                              </TypographyCalibri12Normal>
                            </StyledTableCell>
                            <StyledTableCell
                              sx={{ backgroundColor: '#CCE4F5' }}
                            >
                              <TypographyCalibri12Normal
                                sx={{
                                  verticalAlign: 'middle',
                                  textAlign: 'center',
                                  width: 'auto'
                                }}
                              >
                                {formatNumber(getRniValue(vitamin.key))}
                              </TypographyCalibri12Normal>
                            </StyledTableCell>
                          </StyledTableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </div>
      </>
    );
  };

  const renderMineralsRNITable = () => {
    if (selectedProducts.length === 0) return null;

    const product1 = selectedProducts[0];
    const product2 = selectedProducts.length > 1 ? selectedProducts[1] : null;
    const userVolume1 = p1VolumeCalculationOffset;
    const userVolume2 = p2VolumeCalculationOffset;
    const scaledProduct1 = scaleNutrientValues(product1, userVolume1);
    const scaledProduct2 = product2
      ? scaleNutrientValues(product2, userVolume2)
      : null;

    const minerals = [
      { label: 'Sodium', key: 'sodium' },
      { label: 'Potassium', key: 'potassium' },
      { label: 'Magnesium', key: 'magnesium' },
      { label: 'Phosphorus', key: 'phosphate' },
      { label: 'Calcium', key: 'calcium' },
      { label: 'Chloride', key: 'chloride' },
      { label: 'Iron', key: 'iron' },
      { label: 'Zinc', key: 'zinc' },
      { label: 'Manganese', key: 'manganese' },
      { label: 'Copper', key: 'copper' },
      { label: 'Iodine', key: 'iodine' },
      { label: 'Selenium', key: 'selenium' },
      { label: 'Chromium', key: 'chromium' },
      { label: 'Molybdenum<sup>‡</sup>', key: 'molybdenum' },
      { label: 'Taurine', key: 'taurine' }
    ];

    const rniCategory = getRniCategory();
    const rniHeader =
      rniCategory === 'paed'
        ? 'RNI (Pediatric)'
        : rniCategory === 'rniFemale19To50'
          ? 'RNI Female 19-50'
          : rniCategory === 'rniFemale50Plus'
            ? 'RNI Female 50+'
            : rniCategory === 'rniMale19To50'
              ? 'RNI Male 19-50'
              : 'RNI Male 50+';

    return (
      <>
        <div
          id="section-minerals-1"
          style={{ background: '#fff' }}
          className="pdf-section"
        >
          <Accordion
            id="minerals"
            expanded={expandedAccordions.minerals}
            onChange={handleAccordionChange('minerals')}
            sx={{
              marginTop: '0.625rem',
              marginBottom: '1.25rem',
              boxShadow: 'none',
              border: '0.0625rem solid #8BB5C6',
              breakInside: 'avoid'
            }}
            className="accordion-pdf-spacing"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <TypographyBg20BlueBold>
                MINERALS AND OTHER
              </TypographyBg20BlueBold>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              <TableContainer
                component={Paper}
                sx={{ boxShadow: 'none', border: 'none' }}
              >
                <Table className="pdf-table">
                  <TableHead>
                    <TableRow className="pdf-row">
                      <StyledTableCell></StyledTableCell>
                      <StyledTableCell>
                        <TypographyCalibri12Bold
                          sx={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            width: 'auto'
                          }}
                        >
                          Unit
                        </TypographyCalibri12Bold>
                      </StyledTableCell>
                      <StyledTableCell sx={{ backgroundColor: '#DEF3FA' }}>
                        <TypographyCalibri12Bold
                          sx={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            width: 'auto'
                          }}
                        >
                          {selectedProducts.length > 1
                            ? `${userVolume1} mL ${product1.name} & ${userVolume2} mL ${product2?.name}`
                            : `${userVolume1} mL ${product1.name}`}
                        </TypographyCalibri12Bold>
                      </StyledTableCell>
                      <StyledTableCell sx={{ backgroundColor: '#CCE4F5' }}>
                        <TypographyCalibri12Bold
                          sx={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            width: 'auto'
                          }}
                        >
                          {rniHeader}
                        </TypographyCalibri12Bold>
                      </StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {minerals.map((mineral, index) => {
                      const product1Mineral =
                        scaledProduct1.minerals[mineral.key];
                      const product2Mineral = scaledProduct2?.minerals?.[
                        mineral.key
                      ] || {
                        value: 0,
                        unit: product1Mineral.unit
                      };
                      const summedValue = calculateRNISummedValue(
                        product1Mineral.value,
                        product2Mineral?.value || null
                      );

                      return (
                        <StyledTableRow
                          key={mineral.key}
                          className={index > 7 ? 'pdf-hidden-element' : ''}
                        >
                          <StyledTableCell sx={{ minWidth: '130px' }}>
                            <TypographyCalibri12Normal
                              sx={{
                                verticalAlign: 'middle',
                                textAlign: 'left',
                                width: 'auto'
                              }}
                            >
                              <TypographyCalibri12Normal
                                sx={{
                                  verticalAlign: 'middle',
                                  textAlign: 'left',
                                  width: 'auto'
                                }}
                              >
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: mineral.label
                                  }}
                                ></span>
                              </TypographyCalibri12Normal>
                            </TypographyCalibri12Normal>
                          </StyledTableCell>
                          <StyledTableCell>
                            <TypographyCalibri12Normal
                              sx={{
                                verticalAlign: 'middle',
                                textAlign: 'center',
                                width: 'auto'
                              }}
                            >
                              {product1Mineral.unit}
                            </TypographyCalibri12Normal>
                          </StyledTableCell>
                          <StyledTableCell sx={{ backgroundColor: '#DEF3FA' }}>
                            <TypographyCalibri12Normal
                              sx={{
                                verticalAlign: 'middle',
                                textAlign: 'center',
                                width: 'auto'
                              }}
                            >
                              {summedValue}
                            </TypographyCalibri12Normal>
                          </StyledTableCell>
                          <StyledTableCell sx={{ backgroundColor: '#CCE4F5' }}>
                            <TypographyCalibri12Normal
                              sx={{
                                verticalAlign: 'middle',
                                textAlign: 'center',
                                width: 'auto'
                              }}
                            >
                              {formatNumber(getRniValue(mineral.key))}
                            </TypographyCalibri12Normal>
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </div>
        <div
          id="section-minerals-2"
          className="pdf-section"
          style={{ display: 'none', background: '#fff' }}
        >
          <Accordion
            id="minerals-2"
            expanded={expandedAccordions.minerals2}
            onChange={handleAccordionChange('minerals2')}
            sx={{
              marginTop: '0.625rem',
              marginBottom: '1.25rem',
              boxShadow: 'none',
              border: '0.0625rem solid #8BB5C6',
              breakInside: 'avoid'
            }}
            className="accordion-pdf-spacing"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <TypographyBg20BlueBold>
                MINERALS AND OTHER
              </TypographyBg20BlueBold>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              <TableContainer
                component={Paper}
                sx={{ boxShadow: 'none', border: 'none' }}
              >
                <Table className="pdf-table">
                  <TableHead>
                    <TableRow className="pdf-row">
                      <StyledTableCell></StyledTableCell>
                      <StyledTableCell>
                        <TypographyCalibri12Bold
                          sx={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            width: 'auto'
                          }}
                        >
                          Unit
                        </TypographyCalibri12Bold>
                      </StyledTableCell>
                      <StyledTableCell sx={{ backgroundColor: '#DEF3FA' }}>
                        <TypographyCalibri12Bold
                          sx={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            width: 'auto'
                          }}
                        >
                          {selectedProducts.length > 1
                            ? `${userVolume1} mL ${product1.name} & ${userVolume2} mL ${product2?.name}`
                            : `${userVolume1} mL ${product1.name}`}
                        </TypographyCalibri12Bold>
                      </StyledTableCell>
                      <StyledTableCell sx={{ backgroundColor: '#CCE4F5' }}>
                        <TypographyCalibri12Bold
                          sx={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            width: 'auto'
                          }}
                        >
                          {rniHeader}
                        </TypographyCalibri12Bold>
                      </StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {minerals.map((mineral, index) => {
                      if (index < 8) return null;
                      const product1Mineral =
                        scaledProduct1.minerals[mineral.key];
                      const product2Mineral = scaledProduct2?.minerals?.[
                        mineral.key
                      ] || {
                        value: 0,
                        unit: product1Mineral.unit
                      };
                      const summedValue = calculateRNISummedValue(
                        product1Mineral.value,
                        product2Mineral?.value || null
                      );

                      return (
                        <StyledTableRow key={mineral.key}>
                          <StyledTableCell>
                            <TypographyCalibri12Normal
                              sx={{
                                verticalAlign: 'middle',
                                textAlign: 'center',
                                width: 'auto'
                              }}
                            >
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: mineral.label
                                }}
                              ></span>
                            </TypographyCalibri12Normal>
                          </StyledTableCell>
                          <StyledTableCell>
                            <TypographyCalibri12Normal
                              sx={{
                                verticalAlign: 'middle',
                                textAlign: 'center',
                                width: 'auto'
                              }}
                            >
                              {product1Mineral.unit}
                            </TypographyCalibri12Normal>
                          </StyledTableCell>
                          <StyledTableCell sx={{ backgroundColor: '#DEF3FA' }}>
                            <TypographyCalibri12Normal
                              sx={{
                                verticalAlign: 'middle',
                                textAlign: 'center',
                                width: 'auto'
                              }}
                            >
                              {summedValue}
                            </TypographyCalibri12Normal>
                          </StyledTableCell>
                          <StyledTableCell sx={{ backgroundColor: '#CCE4F5' }}>
                            <TypographyCalibri12Normal
                              sx={{
                                verticalAlign: 'middle',
                                textAlign: 'center',
                                width: 'auto'
                              }}
                            >
                              {formatNumber(getRniValue(mineral.key))}
                            </TypographyCalibri12Normal>
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </div>
      </>
    );
  };

  const handlePDFGeneration = async (
    type,
    filename,
    pdfName = 'ProductsVsRNI'
  ) => {
    setLoading(true);
    const currentStates = { ...expandedAccordions };

    // Expand all accordions
    setExpandedAccordions({
      nutrients: true,
      vitamins: true,
      vitamins2: true,
      minerals: true,
      minerals2: true
    });

    try {
      if (type === 'download') {
        await handleDownloadPdf(filename, 'Products vs RNI');
      } else {
        await handleSharePdf(filename, pdfName, 'Products vs RNI');
      }

      // Collapse accordions
      setExpandedAccordions(currentStates);

      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <>
      <BoxStyled>
        <Box
          sx={{
            display: 'flex',
            paddingTop: '1.25rem',
            gap: '15px'
          }}
        >
          <TypographyStyledHeader
            sx={{
              paddingTop: '1.25rem'
            }}
          >
            PRODUCT VS REQUIREMENTS
          </TypographyStyledHeader>
          <IconButton
            onClick={handleIconClick}
            size="small"
            aria-label="info"
            sx={{
              padding: 0,
              alignItems: 'flex-start'
            }}
          >
            <img src={infoIcon} alt="Info" width="20" height="20" />
          </IconButton>
        </Box>
        <div id="pdf-main-content" style={{ marginTop: '1.875rem' }}>
          {renderMacronutrientsRNITable()}
          {renderVitaminsRNITable()}
          {renderMineralsRNITable()}
        </div>

        <TileSleekGridGroup tileData={rniScreenTileData} />

        <Button
          variant="contained"
          onClick={() =>
            handlePDFGeneration('share', 'ProductsVsRNI.pdf', 'ProductsVsRNI')
          }
          disabled={false}
          endIcon={<img src={EmailResultsIcon} alt="arrow" />}
          sx={{
            borderRadius: '1.25rem',
            height: '3.125rem',
            margin: '0.625rem',
            marginTop: '1.25rem',
            background: '#007acc',
            ':disabled': { color: 'black' }
          }}
          data-testid="pdf-share-button"
        >
          EMAIL RESULTS
        </Button>

        <Button
          variant="contained"
          onClick={() => handlePDFGeneration('download', 'ProductsVsRNI.pdf')}
          disabled={false}
          endIcon={<img src={DownloadIcon} alt="arrow" />}
          sx={{
            borderRadius: '1.25rem',
            height: '3.125rem',
            margin: '0.625rem',
            background: '#007acc',
            ':disabled': { color: 'black' }
          }}
          data-testid="pdf-download-button"
        >
          DOWNLOAD
        </Button>
      </BoxStyled>
      <BoxStyled sx={{ marginTop: '3.125rem' }}>
        <NavigationButtons
          nextDisabled={true}
          nextRoute={'/compare/table'}
          nextVisible={false}
        />
      </BoxStyled>
      <Backdrop
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          color: '#007ACC',
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
        open={loading}
      >
        <CircularProgress
          color="inherit"
          size={60}
          thickness={5}
          sx={{ animationDuration: '800ms' }}
        />
        <TypographyBg20BlueBold sx={{ mt: 2 }}>
          Generating PDF ...
        </TypographyBg20BlueBold>
      </Backdrop>
      <Modal
        open={openInfoModal}
        onClose={handleIconClick}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <TableInfoModal onClose={handleIconClick} />
      </Modal>
    </>
  );
};

export { TableView };
